import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

export type AlertVariant = 'success' | 'warning' | 'error' | 'info'

/**
 * `default` is the standalone alert card. `compact` is the inline callout
 * used inside list rows (e.g. trace log events): tighter padding, a 16px
 * glyph, and emphasized single-line copy.
 */
export type AlertSize = 'default' | 'compact'

// Knockout color for the variant glyphs (success tick, warning exclamation).
// Binds to the `text.on-accent` semantic (near-black in dark, white in light)
// so the knockout reads on the status-tinted fill in both schemes. Applied via
// a Chakra style prop so the token resolves.
export const knockoutColor = {
  dark: 'text.on-accent',
  light: 'light.text.on-accent',
} as const

type VariantTokens = { bg: string; icon: string; desc: string }

// Each variant binds only to semantic tokens (design-library governance:
// never bind to a primitive or raw hex). `bg` tints the surface, `icon`
// colors the leading glyph, `desc` colors the supporting copy.
const tokens: Record<AlertVariant, Record<ColorScheme, VariantTokens>> = {
  success: {
    dark: {
      bg: 'bg.alert-success',
      icon: 'status.success',
      desc: 'status.success-text',
    },
    light: {
      bg: 'light.bg.alert-success',
      icon: 'light.status.success',
      desc: 'light.status.success-text',
    },
  },
  warning: {
    dark: {
      bg: 'bg.alert-warning',
      icon: 'status.warning',
      desc: 'status.warning-text',
    },
    light: {
      bg: 'light.bg.alert-warning',
      icon: 'light.status.warning',
      desc: 'light.status.warning-text',
    },
  },
  error: {
    dark: {
      bg: 'status.error-bg',
      icon: 'status.error',
      desc: 'status.error-text',
    },
    light: {
      bg: 'light.status.error-bg',
      icon: 'light.status.error',
      desc: 'light.status.error-text',
    },
  },
  info: {
    dark: {
      bg: 'status.info-bg',
      icon: 'status.info',
      desc: 'status.info-text',
    },
    light: {
      bg: 'light.status.info-bg',
      icon: 'light.status.info',
      desc: 'light.status.info-text',
    },
  },
}

// `variant` is typed, but TypeScript cannot stop an untyped consumer (JS, or a
// value widened to `string`) from passing something else — and every lookup
// below, plus the glyph map in Alert.tsx, would throw on a miss. Callers
// normalize through this guard so an unknown variant degrades to
// `FALLBACK_ALERT_VARIANT` instead of taking down the tree.
export const FALLBACK_ALERT_VARIANT: AlertVariant = 'warning'

export const ALERT_VARIANTS = Object.keys(tokens) as AlertVariant[]

const variantKeys = new Set<string>(ALERT_VARIANTS)

export const isAlertVariant = (value: unknown): value is AlertVariant =>
  typeof value === 'string' && variantKeys.has(value)

const titleColor = {
  dark: 'text.primary',
  light: 'light.text.primary',
} as const

export const ALERT_GLYPH_SIZE: Record<AlertSize, number> = {
  default: 22,
  compact: 16,
}

export const alertStyles = (
  variant: AlertVariant,
  colorScheme: ColorScheme,
  isCentered = false,
  size: AlertSize = 'default'
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isCentered ? 'center' : 'flex-start',
  gap: size === 'compact' ? '8px' : '11px',
  px: size === 'compact' ? '8px' : '13px',
  py: size === 'compact' ? '8px' : '11px',
  w: 'full',
  bg: tokens[variant][colorScheme].bg,
})

export const alertIconStyles = (
  variant: AlertVariant,
  colorScheme: ColorScheme,
  size: AlertSize = 'default'
): SystemStyleObject => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  h: `${ALERT_GLYPH_SIZE[size]}px`,
  w: `${ALERT_GLYPH_SIZE[size]}px`,
  // The glyph shape renders with `currentColor`, so setting the color here
  // tints the icon per variant.
  color: tokens[variant][colorScheme].icon,
})

// Centered, the body has to shrink to its content so the glyph and the copy
// center as one group; left-aligned it fills the row so wrapped description
// lines start under the title rather than under the glyph.
export const alertBodyStyles = (isCentered = false): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: isCentered ? '0 1 auto' : '1 0 0',
  minW: 0,
  textAlign: isCentered ? 'center' : 'start',
})

// The action button trails the glyph + copy group; it must never be squashed
// by a long title.
export const alertActionStyles: SystemStyleObject = {
  flexShrink: 0,
}

// The dismiss ✕ trails the content as an in-flow flex child, so it reserves
// its own space and content can never render beneath it. `ml: auto` pushes it
// to the right edge of the banner; `flexShrink: 0` keeps it from being squashed
// by a long or localized description.
export const alertDismissStyles: SystemStyleObject = {
  flexShrink: 0,
  ml: 'auto',
  display: 'flex',
  alignItems: 'center',
}

export const alertTitleStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  textStyle: 'label',
  color: titleColor[colorScheme],
  m: 0,
})

export const alertDescStyles = (
  variant: AlertVariant,
  colorScheme: ColorScheme,
  size: AlertSize = 'default'
): SystemStyleObject => ({
  // Compact callouts carry a single emphasized line, so the description takes
  // the medium weight the standalone card reserves for its title.
  textStyle: size === 'compact' ? 'caption-medium' : 'caption',
  color: tokens[variant][colorScheme].desc,
  m: 0,
})
